import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {createTaskAction} from "../../api/task";
import {AddTaskDTO, CompanyType, TaskPriority, TaskType} from "../../api/dtos";
import {TextFieldVariants} from "@mui/material/TextField/TextField";
import ErrorText from "../../components/ErrorText";
import {AsyncTaskStatusType, FieldErrors} from "../../shared/dtos";
import {AppState} from "../../store";
import {getNonCompanyUsersAction, getUsersAction} from "../../api/appUser";
import {setCreateTaskTaskStatus} from "../../features/task/taskSlice";
import CustomDialog from "../../components/CustomDialog";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import {DateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {tr} from "date-fns/locale";
import {setAllPaginatedUsers, setAllUsersLookup} from "../../features/app-user/appUserSlice";
import {getAllCompaniesAction} from "../../api/settings";
import {setAllCompanies} from "../../features/settings/settingsSlice";
import {Autocomplete} from "@mui/lab";

interface CreateTaskDialogProps {
    handleClose: () => void;
    legalCaseId?: number;
    contractId?: number;
    handleCreateTask: () => void;
}

const fieldVariant = "outlined";

export const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
                                                                      handleClose,
                                                                      legalCaseId,
                                                                      contractId,
                                                                      handleCreateTask
                                                                  }) => {
    const dispatch: any = useDispatch();
    const allPaginatedUsers = useSelector((state: AppState) => state.appUser.allPaginatedUsers);
    const nonCompanyUsersLookup = useSelector((state: AppState) => state.appUser.nonCompanyUsersLookup);
    const createTaskTaskStatus = useSelector((state: AppState) => state.task.createTaskTaskStatus);
    const [title, setTitle] = useState("");
    const [priority, setPriority] = useState<TaskPriority | null>(null);
    const [description, setDescription] = useState("");
    const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
    const [dueDate, setDueDate] = useState<string | null>(null);
    const [dateError, setDateError] = useState<boolean>(false);
    const [errors, setErrors] = useState<FieldErrors>({});
    const currentUser = useSelector((state: AppState) => state.auth.userWithToken?.user);
    const associatedCompanyId = currentUser?.associatedCompany?.id;
    const [companyId, setCompanyId] = useState<number | null>(associatedCompanyId || null);
    const [taskType, setTaskType] = useState<TaskType | null>(legalCaseId ? TaskType.LegalCase : contractId ? TaskType.Contract : null);

    useEffect(() => {
        if (associatedCompanyId) {
            dispatch(getUsersAction({companyIdFilter: currentUser?.associatedCompany?.id, page: 1}));
        } else {
            dispatch(getAllCompaniesAction());
            dispatch(getNonCompanyUsersAction());
        }
        return () => {
            dispatch(setCreateTaskTaskStatus(null));
            dispatch(setAllPaginatedUsers(null));
            dispatch(setAllUsersLookup(null));
            dispatch(setAllCompanies(null));
        }
    }, [dispatch]);

    useEffect(() => {
        if (companyId) {
            dispatch(getUsersAction({companyIdFilter: companyId, page: 1}));
        } else {
            dispatch(getNonCompanyUsersAction());
        }
    }, [companyId, dispatch]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!dueDate) {
            setDateError(true);
            return;
        }

        if (priority && taskType) {
            const formJson: AddTaskDTO = {
                title,
                type: taskType,
                priority,
                description,
                legalCaseId: legalCaseId || null,
                contractId: contractId || null,
                assigneeIds,
                dueDate,
                companyId
            };

            const result = await dispatch(createTaskAction(formJson));
            if (result.type === "task/createTask/fulfilled") {
                handleCreateTask();
                handleClose();
            }
        }
    };

    useEffect(() => {
        if (createTaskTaskStatus?.fieldErrors) {
            const updatedErrors: FieldErrors = {};
            createTaskTaskStatus?.fieldErrors.forEach((it) =>
                updatedErrors[it.field] = it.message);
            setErrors(updatedErrors);
        }
    }, [createTaskTaskStatus]);

    const handleClearPriority = () => setPriority(null);
    const handleClearCompanyId = () => setCompanyId(null);

    const handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);

    const handleChangePriority = (e: SelectChangeEvent<TaskPriority | null>) => setPriority(e.target.value as TaskPriority);
    const handleChangeTaskType = (e: SelectChangeEvent<TaskType | null>) => setTaskType(e.target.value as TaskType);
    const allCompanies = useSelector((state: AppState) => state.settings.allCompanies);
    const handleCompanyChange = (event: React.ChangeEvent<{}>, newValue: any) => {
        if (newValue) {
            setCompanyId(newValue.id);
            const relatedAssigneeIds =
                nonCompanyUsersLookup?.filter((userLookup) => !newValue.id || userLookup.associatedCompany?.id === newValue.id)
                    .map((userLookup) => userLookup.id) || [];
            setAssigneeIds(assigneeIds.filter((assigneeId) => relatedAssigneeIds.includes(assigneeId)));
        } else {
            setCompanyId(null);
            setAssigneeIds([]);
        }
    };

    return (
        <CustomDialog
            open={true}
            onClose={handleClose}
            PaperProps={{
                component: 'form',
                onSubmit: handleSubmit,
                sx: {
                    overflow: 'visible',
                },
            }}>
            <DialogTitle>Görev Ekle</DialogTitle>
            <DialogContent>
                <FormControl fullWidth={true} sx={{mb: 1}}>
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        id="title"
                        name="title"
                        label="Görev Adı"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={title}
                        onChange={handleChangeTitle}/>
                    {errors.title && (
                        <ErrorText message={errors.title}/>
                    )}
                </FormControl>

                <FormControl fullWidth={true} sx={{mb: 1}}>
                    <TextField
                        required
                        margin="dense"
                        id="description"
                        name="description"
                        label="Açıklama"
                        type="text"
                        multiline
                        rows={4}
                        fullWidth
                        variant="outlined"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}/>
                    {errors.description && <ErrorText message={errors.description}/>}
                </FormControl>

                <FormControl fullWidth={true} sx={{mt: 2, mb: 1}}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                        <DateTimePicker
                            label="Hedeflenen Tarih"
                            value={dueDate ? new Date(dueDate) : null}
                            onChange={(newDate) => {
                                if (newDate && !isNaN(newDate.getTime())) {
                                    setDueDate(newDate.toISOString());
                                    setDateError(false);
                                } else {
                                    setDueDate(null);
                                    setDateError(true);
                                }
                            }}
                            minDateTime={new Date()}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'preventOverflow',
                                            options: {
                                                boundary: 'viewport',
                                            },
                                        },
                                    ],
                                },
                            }}
                            // @ts-ignore
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    margin="dense"
                                    fullWidth
                                    required
                                    error={dateError}/>
                            )}/>
                    </LocalizationProvider>
                    {dateError && (
                        <ErrorText message={dateError ? 'Son tarih seçilmesi zorunludur' : ''}/>
                    )}
                    {errors.dueDate && (
                        <ErrorText message={errors.dueDate}/>
                    )}
                </FormControl>
                {!associatedCompanyId && (
                    <FormControl fullWidth={true} sx={{mt: 2, mb: 1}}>
                        <Autocomplete
                            id="associated-company-id"
                            options={allCompanies || []}
                            getOptionLabel={(option) => option.name || ""}
                            groupBy={(option) => option.type === CompanyType.Branch ? "ŞUBELER" : "İŞTİRAKLER"}
                            value={allCompanies?.find((company) => company.id === companyId) || null}
                            onChange={handleCompanyChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Şube/İştirakler"
                                    variant="outlined"
                                    error={!!errors.associatedCompanyId}
                                    helperText={errors.associatedCompanyId}/>
                            )}/>
                    </FormControl>
                )}

                <FormControl fullWidth={true} sx={{mt: 2, mb: 1}}>
                    <InputLabel id="assignee-ids-label">Atananlar <span>*</span></InputLabel>
                    <Select
                        required
                        labelId="assignee-ids-label"
                        id="assignee-ids"
                        name="assigneeIds"
                        label="Atananlar"
                        fullWidth
                        multiple={true}
                        variant={fieldVariant as TextFieldVariants}
                        value={assigneeIds}
                        onChange={(event: any) => setAssigneeIds(event.target.value)}>
                        {companyId
                            ? allPaginatedUsers?.entity.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.firstName} {user.lastName}
                                </MenuItem>
                            ))
                            : nonCompanyUsersLookup?.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.value}
                                </MenuItem>
                            ))}
                    </Select>
                    {errors.assigneeIds && (
                        <ErrorText message={errors.assigneeIds}/>
                    )}
                </FormControl>
                <FormControl fullWidth={true} sx={{mt: 2, mb: 1}}>
                    <InputLabel id="task-priority-label">Öncelik <span>*</span></InputLabel>
                    <Select
                        labelId="task-priority-label"
                        required
                        id="priority"
                        name="priority"
                        label="Öncelik"
                        fullWidth
                        value={priority}
                        onChange={handleChangePriority}
                        endAdornment={
                            priority && (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClearPriority}>
                                        <ClearIcon/>
                                    </IconButton>
                                </InputAdornment>
                            )
                        }>
                        <MenuItem value={TaskPriority.Urgent}>Acil</MenuItem>
                        <MenuItem value={TaskPriority.High}>Yüksek</MenuItem>
                        <MenuItem value={TaskPriority.Medium}>Orta</MenuItem>
                        <MenuItem value={TaskPriority.Low}>Düşük</MenuItem>
                    </Select>
                    {errors.priority && (
                        <ErrorText message={errors.priority}/>
                    )}
                </FormControl>
                {!legalCaseId && !contractId && (
                    <FormControl fullWidth={true} sx={{mt: 2, mb: 1}}>
                        <InputLabel id="task-type-label">Görev Tipi <span>*</span></InputLabel>
                        <Select
                            labelId="task-type-label"
                            required
                            id="taskType"
                            name="taskType"
                            label="Görev Tipi"
                            fullWidth
                            value={taskType}
                            onChange={handleChangeTaskType}>
                            <MenuItem value={TaskType.LegalNotice}>Tebligat Görevleri</MenuItem>
                            <MenuItem value={TaskType.General}>Genel Görevler</MenuItem>
                        </Select>
                        {errors.taskType && (
                            <ErrorText message={errors.taskType}/>
                        )}
                    </FormControl>
                )}
                {createTaskTaskStatus?.type === AsyncTaskStatusType.Error && createTaskTaskStatus.errorMessage && (
                    <ErrorText type="form" message={createTaskTaskStatus.errorMessage}/>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>İptal</Button>
                <Button type="submit">Oluştur</Button>
            </DialogActions>
        </CustomDialog>
    );
};
